<template>
  <div class="overrides-editor">
    <!-- Manual table for overrides -->
    <div class="overrides-table-container">
      <div class="overrides-table">
        <!-- Header -->
        <div class="table-header">
          <div class="th col-custom-field">
            {{ t('customData.overrideEditor.labels.customField') }}
          </div>
          <div class="th col-system-field">
            {{ t('customData.overrideEditor.labels.systemField') }}
          </div>
          <div class="th col-actions" />
        </div>

        <!-- Empty state -->
        <div
          v-if="currentRelationOverrides.length === 0 && !showAddingOverrideForm"
          class="empty-state"
        >
          <i class="fas fa-exchange-alt empty-state-icon" />
          <p>{{ t('customData.overrideEditor.emptyMessage') || 'No overrides configured' }}</p>
        </div>

        <!-- Override rows -->
        <div
          v-for="override in currentRelationOverrides"
          :key="override.custom_field_name + '-' + override.system_field_name"
          class="table-row"
        >
          <div class="td col-custom-field">
            <div class="field-display-inline">
              <i class="fas fa-database field-icon field-icon--custom" />
              <span class="field-name field-name--custom">{{ override.custom_field_name }}</span>
            </div>
          </div>
          <div class="td col-system-field">
            <div class="field-display-inline">
              <i class="fas fa-cog field-icon field-icon--system" />
              <span class="field-name field-name--system">{{ override.system_field_name }}</span>
            </div>
          </div>
          <div class="td col-actions">
            <div class="actions-cell">
              <span
                v-tooltip="t('customData.overrideEditor.tooltips.managePriority')"
                class="priority-btn"
                @click="openPriorityModal(override)"
              >
                <i class="fas fa-sort-numeric-down" />
                <span class="priority-badge">{{ override.priority }}</span>
              </span>
              <i
                v-tooltip="t('customData.overrideEditor.tooltips.removeOverride')"
                class="far fa-trash-alt action-icon"
                @click="onRemoveOverride(override)"
              />
            </div>
          </div>
        </div>

        <!-- Add override form (inline row, edit style) -->
        <div
          v-if="showAddingOverrideForm"
          class="table-row row-is-editing"
        >
          <div class="td col-custom-field">
            <BaseDropdown
              v-model="selectedCustomField"
              :options="customOverrideFieldsOptions"
              :placeholder="t('customData.overrideEditor.placeholders.chooseCustomField')"
              class="w-full"
            />
          </div>
          <div class="td col-system-field">
            <BaseDropdown
              v-model="selectedSystemField"
              :options="systemOverrideFieldsOptions"
              :placeholder="t('customData.overrideEditor.placeholders.chooseSystemField')"
              class="w-full"
            />
          </div>
          <div class="td col-actions">
            <div class="actions-cell">
              <i
                v-tooltip="t('customData.overrideEditor.tooltips.saveOverride')"
                class="fas fa-check action-icon"
                @click="onSaveOverride"
              />
              <i
                v-tooltip="t('customData.overrideEditor.tooltips.removeOverride')"
                class="fas fa-times action-icon"
                @click="onRemoveOverride(null)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add override button -->
    <div class="add-override-container">
      <div
        class="add-override-item"
        @click="addOverride"
      >
        <span class="add-override-icon-circle">
          <i class="fas fa-plus" />
        </span>
        <span>{{ t('customData.overrideEditor.buttons.addOverride') }}</span>
      </div>
    </div>
  </div>

  <!-- Priority management modal -->
  <Dialog
    v-model:visible="priorityModalVisible"
    modal
    :header="t('customData.overrideEditor.modal.header')"
    :style="{ width: '550px' }"
    :closable="true"
    class="priority-modal"
  >
    <div class="priority-modal-content">
      <div class="modal-info-banner">
        <i class="fa-solid fa-circle-info" />
        <div>
          <strong>{{ t('customData.overrideEditor.labels.systemFieldLabel') }} <span class="system-field-highlight">{{ overridesToManagePriority[0].system_field_name }}</span></strong>
          <p>{{ t('customData.overrideEditor.modal.description') }}</p>
        </div>
      </div>

      <div class="priority-list">
        <div
          v-for="(item, index) in overridesToManagePriority"
          :key="item.id"
          class="priority-item"
          :class="{ 'priority-item--current': parseInt(item.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition, 10) }"
        >
          <div class="priority-rank">
            {{ index + 1 }}
          </div>
          <div class="priority-item-body">
            <span class="priority-schema-name">{{ getCustomSchemaName(item.id_custom_data_definition) }}</span>
            <span class="priority-separator">·</span>
            <span class="priority-field-name">{{ item.custom_field_name }}</span>
          </div>
          <div class="priority-arrows">
            <i
              v-if="index > 0"
              v-tooltip="t('customData.overrideEditor.tooltips.moveUp')"
              class="fas fa-chevron-up arrow-icon"
              @click="movePriority(index, 'up')"
            />
            <i
              v-if="index < overridesToManagePriority.length - 1"
              v-tooltip="t('customData.overrideEditor.tooltips.moveDown')"
              class="fas fa-chevron-down arrow-icon"
              @click="movePriority(index, 'down')"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="modal-footer">
        <Button
          :label="t('customData.overrideEditor.buttons.save')"
          icon="fas fa-save"
          @click="savePriorities"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Tooltip from 'primevue/tooltip';
import { OVERRIDABLE_FIELDS } from '@/composables/custom-data/overridableSystemFields';
import { Field, ForeignKeyRow } from '@/composables/custom-data/customData.interfaces';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'SchemaOverridesEditor',

  components: {
    BaseDropdown,
    Button,
    Dialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idRelation: {
      type: Number,
      required: true,
    },
  },

  setup(props: { idRelation: number }) {
    const { t } = useI18n();
    const store = useStore();
    const idCustomDataDefinition = computed(() => store.getters['customObject/getIdCustomDataDefinition']);
    const fields = computed(() => store.getters['customObject/getFields']);
    const relationships = computed(() => store.getters['customObject/getRelationships']);
    const currentEditingElement = computed(() => store.getters['customObject/getCurrentEditingElement']);
    const relationshipData = computed(() => store.getters['customObject/getRelationById'](props.idRelation));
    const overrides = computed(() => store.getters['customObject/getOverrides']);

    const selectedCustomField = ref<string>('');
    const selectedSystemField = ref<string>('');
    const overridesToManagePriority = ref<any>(null);
    const priorityModalVisible = ref<boolean>(false);

    const showAddingOverrideForm = computed(() => (currentEditingElement.value
      && currentEditingElement.value.elementType === 'override'));

    const currentRelationOverrides = computed(() => overrides.value.filter((override: any) => override.target_system_schema === relationshipData.value.schema
     && parseInt(override.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10)));

    const customOverrideFieldsOptions = computed(() => fields.value
      .filter((f: Field) => !f.unique_key
          && !relationships.value.some((rel: ForeignKeyRow) => rel.localField === f.name)
          && (!currentRelationOverrides.value.some((override: any) => override.custom_field_name === f.name)
           || (selectedCustomField.value && f.name === selectedCustomField.value)))
      .map((f: Field) => ({ value: f.name, label: f.name })));

    const systemOverrideFieldsOptions = computed(() => {
      if (!selectedCustomField.value || selectedCustomField.value === '') return [];

      const customField = fields.value.find((f: Field) => f.name === selectedCustomField.value);
      if (!customField) return [];

      const targetSchema = relationshipData.value.schema;
      if (!targetSchema) return [];

      const overridableFields = OVERRIDABLE_FIELDS[targetSchema as keyof typeof OVERRIDABLE_FIELDS] || [];

      return overridableFields
        .filter((sf: any) => {
          const systemFieldTypes = Array.isArray(sf.type) ? sf.type : [sf.type];
          return systemFieldTypes.includes(customField.type as string);
        })
        .filter((sf: any) => !currentRelationOverrides.value.some((override: any) => override.system_field_name === sf.field))
        .map((sf: any) => ({ value: sf.field, label: sf.label }));
    });

    const onRemoveOverride = (override: any) => {
      store.dispatch('customObject/handleOverrideAction', {
        action: 'remove',
        id: override ? override.id : null,
      });
    };

    const addOverride = () => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.overrideEditor.messages.cannotAddWhileEditing'), 4000);
        return;
      }
      selectedCustomField.value = '';
      selectedSystemField.value = '';
      store.dispatch('customObject/handleOverrideAction', {
        action: 'add-new-empty',
      });
    };

    const onSaveOverride = () => {
      if (!selectedCustomField.value || selectedCustomField.value === '' || !selectedSystemField.value || selectedSystemField.value === '') {
        showToastError(t('customData.overrideEditor.messages.selectFieldsRequired'), 4000);
        return;
      }
      const itemOverride = {
        id: currentEditingElement.value.id,
        custom_field_name: selectedCustomField.value,
        system_field_name: selectedSystemField.value,
        priority: 0,
        target_system_schema: relationshipData.value.schema,
        id_custom_data_definition: idCustomDataDefinition.value,
      };
      store.dispatch('customObject/handleOverrideAction', {
        action: 'save',
        overrideItem: itemOverride,
      });
    };

    const openPriorityModal = (override: any) => {
      const clonedOverrides = JSON.parse(JSON.stringify(overrides.value));
      overridesToManagePriority.value = clonedOverrides.filter((o: any) => o.target_system_schema === relationshipData.value.schema
        && o.system_field_name === override.system_field_name);
      overridesToManagePriority.value.sort((a: any, b: any) => a.priority - b.priority);
      priorityModalVisible.value = true;
    };

    const movePriority = (index: number, direction: 'up' | 'down') => {
      const clonedOverrides = JSON.parse(JSON.stringify(overridesToManagePriority.value));
      const temp = clonedOverrides[index];
      if (direction === 'up') {
        clonedOverrides[index] = clonedOverrides[index - 1];
        clonedOverrides[index - 1] = temp;
      } else {
        clonedOverrides[index] = clonedOverrides[index + 1];
        clonedOverrides[index + 1] = temp;
      }
      const updatedOverrides = clonedOverrides.map((o: any, index2: number) => ({
        ...o,
        priority: index2 + 1,
      }));
      overridesToManagePriority.value = updatedOverrides;
    };

    const savePriorities = () => {
      store.dispatch('customObject/handleOverrideAction', {
        action: 'save-priorities',
        overridesPriority: overridesToManagePriority.value,
      });
      overridesToManagePriority.value = null;
      priorityModalVisible.value = false;
    };

    const getCustomSchemaName = (id: number) => {
      if (parseInt(id.toString(), 10) === 0) {
        const currentObjectName = store.getters['customObject/getCurrentObjectName'];
        return currentObjectName && currentObjectName !== '' ? currentObjectName : t('customData.overrideEditor.defaults.currentObject');
      }
      const customSchema = store.getters['customObject/getCustomSchemaById'](id);
      return customSchema ? customSchema.label : '';
    };

    return {
      t,
      currentRelationOverrides,
      selectedCustomField,
      selectedSystemField,
      showAddingOverrideForm,
      onRemoveOverride,
      customOverrideFieldsOptions,
      systemOverrideFieldsOptions,
      addOverride,
      onSaveOverride,
      openPriorityModal,
      overridesToManagePriority,
      priorityModalVisible,
      idCustomDataDefinition,
      movePriority,
      savePriorities,
      getCustomSchemaName,
    };
  },
});
</script>

<style lang="scss" scoped>
.overrides-editor {
  padding: 0.5rem 0.75rem;
}

/* Table container */
.overrides-table-container {
  border-radius: 0.35rem;
  overflow: hidden;
  border: 1px solid #e9ecef;
  background: #fff;
}

.overrides-table {
  width: 100%;
}

/* Header */
.table-header {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;

  .th {
    color: #999;
    font-weight: 600;
    font-size: 0.7em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.45rem 0.75rem;
  }

  .col-actions {
    min-width: 80px;
  }
}

/* Rows */
.table-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  background-color: #ffffff;
  border-bottom: 1px solid #f5f5f5;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background-color: #fffcf5;
  }

  &:last-child {
    border-bottom: none;
  }

  &.row-is-editing {
    background-color: #f0f8e8 !important;
    border-left: 3px solid #94c840;
  }

  .td {
    padding: 0.5rem 0.75rem;
  }

  .col-actions {
    min-width: 80px;
  }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  color: #ccc;

  .empty-state-icon {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    opacity: 0.4;
  }

  p {
    margin: 0;
    font-style: italic;
    font-size: 0.85em;
  }
}

/* Field display inline */
.field-display-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .field-icon {
    font-size: 0.8rem;
    opacity: 0.7;

    &--custom {
      color: #1976d2;
    }

    &--system {
      color: #7b1fa2;
    }
  }

  .field-name {
    font-weight: 600;
    font-size: 0.85rem;
    font-family: 'Courier New', monospace;

    &--custom {
      color: #1976d2;
    }

    &--system {
      color: #7b1fa2;
    }
  }
}

/* Actions — gray icons matching SchemaFieldsEditor ellipsis style */
.actions-cell {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
}

.action-icon {
  cursor: pointer;
  color: #888;
  width: 2.2rem;
  height: 2.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  background: transparent;

  &:hover {
    color: #555;
    background-color: #e9ecef;
  }
}

/* Priority button */
.priority-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  cursor: pointer;
  color: #888;
  height: 2.2rem;
  padding: 0 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  background: transparent;

  &:hover {
    color: #555;
    background-color: #e9ecef;
  }
}

.priority-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
}

/* Add button */
.add-override-container {
  margin-top: 0.5rem;
}

.add-override-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  font-weight: 500;
  font-size: 0.85rem;
  border: 1px dashed #d4d4d4;
  color: #888;
  background-color: transparent;

  &:hover {
    border-color: #f59e0b;
    color: #f59e0b;
    background-color: #fffbeb;

    .add-override-icon-circle {
      background-color: #f59e0b;
      color: #fff;
    }
  }
}

.add-override-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #888;
  font-size: 0.6rem;
  transition: all 0.25s ease;
}

/* Priority Modal */
.priority-modal-content {
  padding: 0;
}

.modal-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background-color: #eff6ff;
  border-left: 3px solid #3b82f6;
  border-radius: 4px;
  margin-bottom: 1.25rem;

  > i {
    color: #3b82f6;
    margin-top: 0.15rem;
    flex-shrink: 0;
  }

  strong {
    display: block;
    color: #333;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }

  p {
    margin: 0;
    color: #1e40af;
    font-size: 0.8rem;
    line-height: 1.5;
  }
}

.system-field-highlight {
  color: #7b1fa2;
  background: #f3e5f5;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
}

.priority-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.priority-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #fafafa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d0d0d0;
  }

  &--current {
    background-color: #fffbeb;
    border-color: #fed7aa;

    &:hover {
      background-color: #fef3c7;
      border-color: #fdba74;
    }
  }
}

.priority-rank {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-right: 1rem;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.25);
}

.priority-item-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.priority-schema-name {
  font-weight: 500;
  color: #4b5563;
  font-size: 0.9rem;
}

.priority-separator {
  color: #d1d5db;
  font-size: 1.1rem;
}

.priority-field-name {
  background-color: #dbeafe;
  color: #3b82f6;
  font-weight: 500;
  font-size: 0.8rem;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', Courier, monospace;
}

.priority-arrows {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.arrow-icon {
  cursor: pointer;
  color: #f59e0b;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.85rem;

  &:hover {
    color: #f97316;
    background: #fffbeb;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

:deep(.priority-modal) {
  .p-dialog-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-bottom: 1px solid #e9ecef;

    .p-dialog-title {
      font-weight: 600;
      color: #333;
    }
  }

  .p-dialog-content {
    padding: 1.25rem;
  }

  .p-dialog-footer {
    padding: 0.75rem 1.25rem;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
  }
}

/* Utilities */
.w-full {
  width: 100%;
}
</style>
